import { MetricField, MetricUnit } from "@/__generated__/graphql";
import { useMinichartOptions } from "@/hooks/useMinichartOptions";
import ECharts from "@features/echarts";
import { Trans } from "@lingui/macro";
import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { AlertConfigurationForm } from "../schemas/alertConfigurationSchema";

interface MarkLineConfig {
  name: string;
  yAxis: number;
  lineStyle: {
    color: string;
    type: 'dashed' | 'solid';
    width: number;
  };
  label: {
    show: boolean;
    position: string;
    formatter: string;
    color: string;
    fontSize: number;
    fontWeight?: string;
    backgroundColor?: string;
    padding?: number[];
    borderRadius?: number;
  }
}

const createMarkLine = (
  yValue: number,
  label: string,
  color: string,
  position: 'insideStart' | 'insideEnd' = 'insideStart',
): MarkLineConfig => ({
  name: label,
  yAxis: yValue,
  lineStyle: {
    color,
    type: 'dashed',
    width: 2,
  },
  label: {
    show: true,
    position,
    formatter: label,
    color,
    fontSize: 14,
    fontWeight: 'bold',
    backgroundColor: '#2341a4cc',
  }
})

const generateThresholdMarkLine = (
  config: AlertConfigurationForm['configurations'][number],
  color: string,
  label: string,
): MarkLineConfig[] => {
  if (config.criteria !== 'threshold') return [];

  const { threshold } = config;
  return threshold !== null && threshold !== undefined ? [createMarkLine(threshold, `${label}: ${threshold}`, color)] : [];
}

const generateRangeMarkLine = (
  config: AlertConfigurationForm['configurations'][number],
  color: string,
  label: string,
): MarkLineConfig[] => {
  if (config.criteria !== 'range') return [];

  const { minimum, maximum } = config;
  const lines: MarkLineConfig[] = [];

  if (minimum !== null && minimum !== undefined) {
    lines.push(createMarkLine(minimum, `Min: ${minimum}`, color));
  }

  if (maximum !== null && maximum !== undefined) {
    lines.push(createMarkLine(maximum, `Max: ${maximum}`, color));
  }
  return lines
}

const ALERT_TYPE_COLORS = {
  critical: '#f44336',
  warning: '#ff9800',
  ok: '#4caf50',
  default: '#464444ff',
}

const ALERT_TYPE_LABELS = {
  critical: 'Crítico',
  warning: 'Advertencia',
  ok: 'Ok',
  default: 'Desconocido',
}

const getAlertTypeStyle = (type: 'critical' | 'warning' | 'ok') => {
  const color = ALERT_TYPE_COLORS[type] || ALERT_TYPE_COLORS.default;
  const label = ALERT_TYPE_LABELS[type] || ALERT_TYPE_LABELS.default;

  return { color, label }
}

interface AlertConfigurationPreviewProps {
  deviceId: string;
  metric: MetricField;
  configurations: AlertConfigurationForm['configurations'];
  historicalData?: [number, number][];
  unit: MetricUnit | null;
}

export const AlertConfigurationPreview = ({
  deviceId,
  metric,
  configurations,
  historicalData,
  unit,
}: AlertConfigurationPreviewProps) => {
  const minichartData = {
    [metric.toLowerCase()]: {
      data: historicalData ?? null,
      unit: unit,
      precision: 2
    },
    availableFields: [metric], 
  }

  const baseOption = useMinichartOptions(minichartData, {
    limitPoints: 100,
    enableTooltip: true,
    enableZoom: false,
  });

  const enhancedOption = useMemo(() => {
    if(!baseOption || !configurations || configurations.length === 0) {
      return baseOption;
    };

    const markLines: MarkLineConfig[] = [];

    configurations.forEach((config) => {
      const { color, label } = getAlertTypeStyle(config.alertType.toLowerCase() as 'critical' | 'warning' | 'ok');
      if (config.criteria === 'threshold') {
        markLines.push(...generateThresholdMarkLine(config, color, label));
      } else if (config.criteria === 'range') {
        markLines.push(...generateRangeMarkLine(config, color, label));
      }
    });

    if (markLines.length === 0) {
      return baseOption;
    }

    const modifiedOption = baseOption.clone();
    const seriesId = metric.toLowerCase();

    modifiedOption.updateSerie(seriesId, (serie) => ({
      ...serie,
      markLine: {
        symbol: 'none',
        data: markLines,
        silent: false,
        animation: false
      },
    }));
  
    return modifiedOption;
  }, [baseOption, historicalData, configurations]);


  if (!enhancedOption) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          height: '200px',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Typography color="textSecondary">
          <Trans>No hay datos disponibles para previsualizar</Trans>
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1,
        // height: '200px',
      }}
    >
      <Typography variant='h6'>
        <Trans>Previsualización de las configuraciones</Trans>
      </Typography>
      <Box sx={{ height: '200px' }}>
        <ECharts option={enhancedOption} />
      </Box>
    </Box>
  );
}